import { Chip } from "@legal-500/legal500-design-system";
import type { Feature } from "../api/features.service";
import type { EntitySchema } from "./fieldSchema";
import styles from "../styles/chips.module.css";

// Render helpers - properly typed to exact field values
const renderTypeChip = (value: Feature["type"]): React.ReactNode => (
  <Chip size="small" variant="filled" className={styles.chipBlue}>
    {value}
  </Chip>
);

const renderScopeChip = (value: Feature["scope"]): React.ReactNode => (
  <Chip size="small" variant="filled" className={styles.chipPurple}>
    {value || "none"}
  </Chip>
);

const renderEntityIdChip = (_: Feature["entity_id_field"], row: Feature): React.ReactNode => (
  <Chip size="small" variant="filled" className={styles.chipOrange}>
    {row.entity_id_field || "none"}
  </Chip>
);

const renderArrayChips = (
  value: string[] | null | undefined,
  chipClass: string,
): React.ReactNode => {
  // Per spec: null = universal (works on all)
  if (!value || value.length === 0) {
    return (
      <Chip size="small" variant="filled" className={chipClass}>
        all
      </Chip>
    );
  }

  return (
    <div className={styles.chipList}>
      {value.map(item => (
        <Chip key={item} size="small" variant="filled" className={chipClass}>
          {item}
        </Chip>
      ))}
    </div>
  );
};

const renderStatusChip = (value: Feature["status"]): React.ReactNode => (
  <Chip
    size="small"
    variant="outlined"
    className={value === "active" ? styles.chipStatusActive : styles.chipStatusDeprecated}
  >
    {value}
  </Chip>
);

export const featureSchema: EntitySchema<Feature> = {
  code: {
    label: "Code",
    sortable: true,
    sortField: "code",
  },
  name: {
    label: "Name",
    sortable: true,
    sortField: "name",
  },
  description: {
    label: "Description",
  },
  type: {
    label: "Type",
    render: renderTypeChip,
    sortable: true,
    sortField: "type",
  },
  scope: {
    label: "Scope",
    render: renderScopeChip,
    sortable: true,
    sortField: "scope",
  },
  available_page_types: {
    label: "Page Types",
    render: value => renderArrayChips(value, styles.chipGreen),
  },
  available_sites: {
    label: "Sites",
    render: value => renderArrayChips(value, styles.chipTeal),
  },
  available_languages: {
    label: "Languages",
    render: value => renderArrayChips(value, styles.chipPink),
  },
  entity_id_field: {
    label: "Metadata Required",
    render: renderEntityIdChip,
    sortable: true,
    sortField: "entity_id_field",
  },
  entity_id_label: {
    label: "Metadata Label",
  },
  entity_id_help_text: {
    label: "Metadata Help Text",
  },
  status: {
    label: "Status",
    render: renderStatusChip,
    sortable: true,
    sortField: "status",
  },
};
